import React from 'react';
import { Phone, Wrench, RefreshCw, Settings, Cog, Wind, ArrowLeftRight, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { BUSINESS_INFO, SERVICES, IMAGES } from '@/lib/constants';

const iconMap = {
    Wrench: Wrench,
    RefreshCw: RefreshCw,
    Settings: Settings,
    Cog: Cog,
    Wind: Wind,
    ArrowLeftRight: ArrowLeftRight,
};

const ServicesPage = () => {
    const getWhatsAppMessage = (serviceName) => {
        return encodeURIComponent(`Merhaba, ${serviceName} hizmeti hakkında bilgi almak ve randevu oluşturmak istiyorum.`);
    };

    return (
        <div>
            {/* Hero Section */}
            <section className="relative overflow-hidden bg-muted">
                <div className="container-custom py-16 md:py-20">
                    <div className="max-w-3xl">
                        <h1 className="text-4xl sm:text-5xl font-heading font-bold text-foreground mb-6">
                            Profesyonel Klima Hizmetleri
                        </h1>
                        <p className="text-lg text-muted-foreground leading-relaxed">
                            Airfel yetkili bayi ve servis olarak Antalya'da tüm klima markalarında 
                            satış, montaj, bakım ve onarım hizmetleri sunuyoruz. Uzman ekibimizle 
                            klimanızın en iyi performansı göstermesini sağlıyoruz.
                        </p>
                    </div>
                </div>
            </section>

            {/* Services Grid */}
            <section className="section-padding">
                <div className="container-custom">
                    <div className="grid gap-8">
                        {SERVICES.map((service, index) => {
                            const IconComponent = iconMap[service.icon];
                            const isEven = index % 2 === 0;

                            return (
                                <Card 
                                    key={service.id}
                                    className="overflow-hidden border-border/50"
                                >
                                    <div className={`grid md:grid-cols-2 gap-0 ${!isEven ? 'md:flex-row-reverse' : ''}`}>
                                        {/* Content */}
                                        <div className="p-6 md:p-8 lg:p-10 flex flex-col">
                                            <div className="flex items-start gap-4 mb-6">
                                                <div className="shrink-0 w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center">
                                                    {IconComponent && <IconComponent className="h-7 w-7 text-primary" />}
                                                </div>
                                                <div>
                                                    <h2 className="font-heading font-bold text-2xl text-foreground mb-2">
                                                        {service.title}
                                                    </h2>
                                                    <p className="text-muted-foreground leading-relaxed">
                                                        {service.description}
                                                    </p>
                                                </div>
                                            </div>

                                            <div className="mb-8">
                                                <h3 className="font-semibold text-foreground mb-4">Bu hizmette neler sunuyoruz:</h3>
                                                <ul className="space-y-3">
                                                    {service.features.map((feature, idx) => (
                                                        <li key={idx} className="flex items-center gap-3">
                                                            <CheckCircle2 className="h-5 w-5 text-primary shrink-0" />
                                                            <span className="text-muted-foreground">{feature}</span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>

                                            <div className="mt-auto flex flex-col sm:flex-row gap-3">
                                                <Button variant="whatsapp" size="lg" asChild>
                                                    <a 
                                                        href={`https://wa.me/${BUSINESS_INFO.whatsapp}?text=${getWhatsAppMessage(service.title)}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                                                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                                                        </svg>
                                                        Randevu Al
                                                    </a>
                                                </Button>
                                                <Button variant="call" size="lg" asChild>
                                                    <a href={`tel:${BUSINESS_INFO.phone}`}>
                                                        <Phone className="h-5 w-5" />
                                                        Hemen Ara
                                                    </a>
                                                </Button>
                                            </div>
                                        </div>

                                        {/* Image */}
                                        <div className={`bg-muted min-h-[250px] md:min-h-0 ${!isEven ? 'md:order-first' : ''}`}>
                                            <img 
                                                src={index % 5 === 0 ? IMAGES.hero : index % 5 === 1 ? IMAGES.showroom : index % 5 === 2 ? IMAGES.acUnit : index % 5 === 3 ? IMAGES.products : IMAGES.storefront}
                                                alt={service.title}
                                                className="w-full h-full object-cover"
                                            />
                                        </div>
                                    </div>
                                </Card>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="section-padding bg-primary">
                <div className="container-custom text-center">
                    <h2 className="text-3xl md:text-4xl font-heading font-bold text-primary-foreground mb-4">
                        Hangi Hizmeti Arıyorsunuz?
                    </h2>
                    <p className="text-primary-foreground/80 text-lg mb-8 max-w-2xl mx-auto">
                        Size en uygun hizmeti belirlemek için hemen bizimle iletişime geçin. 
                        Ücretsiz keşif ve fiyat teklifi alın.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <Button 
                            size="xl" 
                            className="bg-background text-primary hover:bg-background/90 font-semibold shadow-lg"
                            asChild
                        >
                            <a href={`tel:${BUSINESS_INFO.phone}`}>
                                <Phone className="h-5 w-5" />
                                {BUSINESS_INFO.phoneDisplay}
                            </a>
                        </Button>
                        <Button variant="whatsapp" size="xl" asChild>
                            <a 
                                href={`https://wa.me/${BUSINESS_INFO.whatsapp}?text=Merhaba, klima hizmeti hakkında bilgi almak istiyorum.`}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                                </svg>
                                WhatsApp İle Yazın
                            </a>
                        </Button>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default ServicesPage;
